import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Users, Car, Wrench, FileText, Sparkles, Settings as SettingsIcon,
  LogOut, Sun, Moon, Search, Mail, Banknote, Calculator, Archive as ArchiveIcon, Timer, Menu, X, Star, StickyNote,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import SyncManager from "@/components/SyncManager";
import GlobalTimerBadge from "@/components/GlobalTimerBadge";
import InstallBanner from "@/components/InstallBanner";
import NotificationBell from "@/components/NotificationBell";
import { ColorChip } from "@/components/ColorChip";

// Sprint 11 — daily workflow first, financial + archival last.
const NAV = [
  { to: "/kalender", label: "Kalender", icon: CalendarDays, testid: "nav-calendar", iconCls: "bg-blue-50 text-blue-600", activeCls: "bg-blue-50 text-blue-800 ring-blue-100" },
  { to: "/auftraege", label: "Aufträge", icon: Wrench, testid: "nav-jobs", iconCls: "bg-emerald-50 text-emerald-600", activeCls: "bg-emerald-50 text-emerald-800 ring-emerald-100" },
  { to: "/werkstatt", label: "Werkstatt-Modus", icon: Timer, testid: "nav-workshop", iconCls: "bg-orange-50 text-orange-600", activeCls: "bg-orange-50 text-orange-800 ring-orange-100" },
  { to: "/rechnungen", label: "Rechnungen", icon: FileText, testid: "nav-invoices", iconCls: "bg-violet-50 text-violet-600", activeCls: "bg-violet-50 text-violet-800 ring-violet-100" },
  { to: "/zahlungen", label: "Zahlungen", icon: Banknote, testid: "nav-payments", iconCls: "bg-teal-50 text-teal-600", activeCls: "bg-teal-50 text-teal-800 ring-teal-100" },
  { to: "/steuer", label: "Steuer", icon: Calculator, testid: "nav-steuer", iconCls: "bg-lime-50 text-lime-700", activeCls: "bg-lime-50 text-lime-900 ring-lime-100" },
  { to: "/kunden", label: "Kunden", icon: Users, testid: "nav-customers", iconCls: "bg-indigo-50 text-indigo-600", activeCls: "bg-indigo-50 text-indigo-800 ring-indigo-100" },
  { to: "/fahrzeuge", label: "Fahrzeuge", icon: Car, testid: "nav-vehicles", iconCls: "bg-sky-50 text-sky-600", activeCls: "bg-sky-50 text-sky-800 ring-sky-100" },
  { to: "/dashboard", label: "Übersicht", icon: LayoutDashboard, testid: "nav-dashboard", iconCls: "bg-cyan-50 text-cyan-600", activeCls: "bg-cyan-50 text-cyan-800 ring-cyan-100" },
  { to: "/emails", label: "E-Mails", icon: Mail, testid: "nav-emails", iconCls: "bg-fuchsia-50 text-fuchsia-600", activeCls: "bg-fuchsia-50 text-fuchsia-800 ring-fuchsia-100" },
  { to: "/notizen", label: "Notizen", icon: StickyNote, testid: "nav-notes", iconCls: "bg-amber-50 text-amber-600", activeCls: "bg-amber-50 text-amber-800 ring-amber-100" },
  { to: "/archiv", label: "Archiv", icon: ArchiveIcon, testid: "nav-archive", iconCls: "bg-slate-100 text-slate-600", activeCls: "bg-slate-100 text-slate-900 ring-slate-200" },
  { to: "/einstellungen", label: "Einstellungen", icon: SettingsIcon, testid: "nav-settings", iconCls: "bg-stone-100 text-stone-600", activeCls: "bg-stone-100 text-stone-900 ring-stone-200" },
];

function SearchBox() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["search", q],
    queryFn: async () => (await api.get(`/search?q=${encodeURIComponent(q)}`)).data,
    enabled: q.length >= 2,
  });
  const total = data ? (data.customers?.length || 0) + (data.appointments?.length || 0)
    + (data.vehicles?.length || 0) + (data.jobs?.length || 0) + (data.invoices?.length || 0) : 0;
  return (
    <div className="relative w-full max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        data-testid="global-search"
        placeholder="Suche nach Kunde, Kennzeichen, Auftrag, RE-Nr…"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className="pl-9"
      />
      {open && q.length >= 2 && data && (
        <div className="absolute top-full left-0 right-0 mt-2 rounded-md border bg-popover shadow-lg z-50 max-h-[70vh] overflow-auto">
          {data.customers?.length > 0 && (
            <div className="p-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1">Kunden</div>
              {data.customers.map((c) => (
                <button
                  key={c.id}
                  data-testid={`search-customer-${c.id}`}
                  onMouseDown={() => navigate(`/kunden/${c.id}`)}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm"
                >
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.phone} · {c.plates?.join(", ")}</div>
                </button>
              ))}
            </div>
          )}
          {data.vehicles?.length > 0 && (
            <div className="p-2 border-t">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1">Fahrzeuge</div>
              {data.vehicles.map((v) => (
                <button
                  key={v.id}
                  data-testid={`search-vehicle-${v.id}`}
                  onMouseDown={() => navigate(`/fahrzeuge/${v.id}`)}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm"
                >
                  <div className="font-medium">{v.manufacturer} {v.model} <span className="text-xs text-muted-foreground ml-1">{v.year && `· Bj. ${v.year}`} {v.license_plate && `· ${v.license_plate}`}</span>{v.color && <span className="ml-1 text-xs text-muted-foreground">· <ColorChip value={v.color} /></span>}</div>
                  <div className="text-xs text-muted-foreground">{v.customer_name || "—"}</div>
                </button>
              ))}
            </div>
          )}
          {data.jobs?.length > 0 && (
            <div className="p-2 border-t">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1">Aufträge</div>
              {data.jobs.map((j) => (
                <button
                  key={j.id}
                  data-testid={`search-job-${j.id}`}
                  onMouseDown={() => navigate(`/auftraege/${j.id}`)}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm"
                >
                  <div className="font-medium">{j.title || j.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{j.customer_name} · {j.status}</div>
                </button>
              ))}
            </div>
          )}
          {data.invoices?.length > 0 && (
            <div className="p-2 border-t">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1">Rechnungen</div>
              {data.invoices.map((i) => (
                <button
                  key={i.id}
                  data-testid={`search-invoice-${i.id}`}
                  onMouseDown={() => navigate(`/rechnungen/${i.id}`)}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm"
                >
                  <div className="font-mono font-medium">{i.invoice_number}</div>
                  <div className="text-xs text-muted-foreground">{i.customer_snapshot?.name} · {i.status}</div>
                </button>
              ))}
            </div>
          )}
          {data.appointments?.length > 0 && (
            <div className="p-2 border-t">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1">Termine</div>
              {data.appointments.map((a) => (
                <button
                  key={a.id}
                  data-testid={`search-appt-${a.id}`}
                  onMouseDown={() => navigate(`/kalender?open=${a.id}`)}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm"
                >
                  <div className="font-medium">{a.customer_name} — {a.service_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(a.start).toLocaleString("de-DE")} · {a.plate}
                  </div>
                </button>
              ))}
            </div>
          )}
          {total === 0 && (
            <div className="p-4 text-sm text-muted-foreground">Keine Ergebnisse</div>
          )}
        </div>
      )}
    </div>
  );
}

function OfflineBadge() {
  return null; // Replaced by SyncManager
}

export default function Layout({ children }) {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { data: headerEmails = [] } = useQuery({
    queryKey: ["emails"],
    queryFn: async () => (await api.get("/email/messages")).data,
    enabled: !!user,
    refetchInterval: 60000,
    staleTime: 15000,
  });
  const unreadEmailCount = headerEmails.filter((message) => !message.read).length;
  useEffect(() => setMounted(true), []);

  const navLink = (n, isMobile) => (
    <NavLink
      key={n.to}
      to={n.to}
      end={n.to === "/"}
      data-testid={isMobile ? `mobile-${n.testid}` : n.testid}
      onClick={() => setMobileNavOpen(false)}
      className={({ isActive }) =>
        `group flex min-h-12 items-center gap-3 rounded-xl px-2.5 py-2 text-[15px] font-semibold transition-all ${
          isActive ? `${n.activeCls} ring-1 shadow-sm` : "text-slate-700 hover:bg-slate-50 hover:text-slate-950"
        }`
      }
    >
      <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${n.iconCls} transition-transform group-hover:scale-105`}>
        <n.icon className="h-[18px] w-[18px]" strokeWidth={2.1} />
      </span>
      <span>{n.label}</span>
    </NavLink>
  );

  return (
    <div className="flex h-screen w-full bg-[#f7f9fb] text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-56 lg:w-64 flex-col border-r border-slate-200/80 bg-white shrink-0">
        <div className="px-7 py-6 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Star className="h-9 w-9 text-emerald-600" strokeWidth={2.2} />
            <div>
              <div className="font-display font-semibold tracking-tight text-base leading-none">Stellar</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Aufbereitung</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => navLink(n, false))}
        </nav>
        <div className="p-4 border-t border-slate-100 space-y-3">
          <div className="premium-card p-3">
            <div className="flex items-center gap-2 text-sm font-semibold"><Star className="h-5 w-5 text-emerald-600" /> Stellar Aufbereitung</div>
            <div className="text-[11px] text-muted-foreground truncate mt-1 pl-7">{user?.email}</div>
          </div>
          <div className="flex gap-2">
            {mounted && (
              <Button
                data-testid="theme-toggle"
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            )}
            <Button data-testid="logout-btn" variant="outline" size="sm" onClick={logout} className="flex-1">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile hamburger drawer */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col">
          <SheetHeader className="px-5 py-4 border-b border-border/60">
            <SheetTitle asChild>
              <div className="flex items-center gap-2">
                <Star className="h-8 w-8 text-emerald-600" />
                <div>
                  <div className="font-display font-semibold tracking-tight text-base leading-none text-left">Stellar</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 text-left">Aufbereitung</div>
                </div>
              </div>
            </SheetTitle>
          </SheetHeader>
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {NAV.map((n) => navLink(n, true))}
          </nav>
          <div className="p-3 border-t border-border/60 space-y-2">
            <div className="text-xs text-muted-foreground truncate px-2">{user?.email}</div>
            <div className="flex gap-2">
              {mounted && (
                <Button variant="outline" size="sm" className="flex-1"
                  onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
                  {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => { setMobileNavOpen(false); logout(); }} className="flex-1">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex flex-1 flex-col min-w-0">
        <header className="flex items-center gap-2 sm:gap-3 border-b border-slate-200/80 bg-white/95 backdrop-blur px-3 sm:px-4 md:px-7 py-3.5">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            data-testid="mobile-menu-open"
            className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-border hover:bg-muted"
            aria-label="Menü öffnen"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden md:block flex-1"><SearchBox /></div>
          <Star className="md:hidden h-6 w-6 text-emerald-600 mx-auto" />
          <div className="ml-auto flex items-center gap-2 sm:gap-3 shrink-0">
            <button
              type="button"
              onClick={() => navigate("/emails")}
              data-testid="header-emails"
              title="E-Mails öffnen"
              aria-label={unreadEmailCount ? `E-Mails öffnen, ${unreadEmailCount} ungelesen` : "E-Mails öffnen"}
              className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <Mail className="h-4 w-4" />
              {unreadEmailCount > 0 && (
                <span className="absolute -right-1.5 -top-1.5 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white shadow-sm" data-testid="header-email-unread-count">
                  {unreadEmailCount > 99 ? "99+" : unreadEmailCount}
                </span>
              )}
            </button>
            <NotificationBell />
            <GlobalTimerBadge />
            <SyncManager />
          </div>
        </header>
        <main className="flex-1 overflow-auto min-w-0">{children}</main>
      </div>
      <InstallBanner />
    </div>
  );
}
